import { useState, useEffect, useRef, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { DEFAULT_APP_COLORS, APP_COLOR_GROUPS, getColorLabel } from '../lib/default-app-colors';
import { ChevronUpIcon, ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';

interface Category {
  _id: string;
  name: string;
  nameArabic?: string;
  type: string;
  icon?: string;
}

interface PaymentMethodsConfig {
  cod?: boolean;
  instapay?: boolean;
  wallet?: boolean;
  paymob?: boolean;
}

interface DeliveryEstimatesValue {
  mealBoxMinHours: number;
  mealBoxMaxHours: number;
  preparedMealMinHours: number;
  preparedMealMaxHours: number;
}

interface ChefBadgeLandingValue {
  heroImageUrl?: string;
  imageBorderRadius?: number;
  headline?: string;
  description?: string;
}

/** Last-step legal copy + post-submit success screen — `chefApplicationConfig` app setting. */
interface ChefApplicationConfigValue {
  termsTitle?: string;
  termsBody?: string;
  guidelinesTitle?: string;
  guidelinesBody?: string;
  successHeroImageUrl?: string;
  successReviewDurationText?: string;
  successDescriptionBefore?: string;
  successDescriptionAfter?: string;
  /** Full days after rejection before the user can apply again (0–365). */
  reapplyCooldownDays?: number;
}

interface AppSettings {
  orderingEnabled: boolean;
  /** Home chef-on-demand card + profile chef bookings / client bookings / chef settings */
  chefOnDemandEnabled?: boolean;
  shippingCost?: number;
  serviceFee?: number;
  vatPercentage?: number;
  vatEnabled?: boolean;
  defaultDeliveryMinutes?: number;
  deliveryEstimates?: DeliveryEstimatesValue;
  paymentMethods?: PaymentMethodsConfig;
  appColors?: Record<string, string>;
  chefBadgeLanding?: ChefBadgeLandingValue;
  chefApplicationConfig?: ChefApplicationConfigValue;
  [key: string]: any;
}

const DEFAULT_PAYMENT_METHODS: PaymentMethodsConfig = { cod: true, instapay: true, wallet: true, paymob: true };

const DEFAULT_DELIVERY_ESTIMATES: DeliveryEstimatesValue = {
  mealBoxMinHours: 24,
  mealBoxMaxHours: 48,
  preparedMealMinHours: 2,
  preparedMealMaxHours: 4,
};

const EMPTY_CHEF_APPLICATION_FORM = {
  termsTitle: '',
  termsBody: '',
  guidelinesTitle: '',
  guidelinesBody: '',
  successHeroImageUrl: '',
  successReviewDurationText: '',
  successDescriptionBefore: '',
  successDescriptionAfter: '',
  reapplyCooldownDays: 14,
};

function parseReapplyCooldownDays(v: unknown): number {
  const n = typeof v === 'number' ? v : parseInt(String(v ?? ''), 10);
  if (Number.isNaN(n)) return 14;
  return Math.min(365, Math.max(0, Math.round(n)));
}

function parsePositiveHour(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : parseInt(String(v), 10);
  if (Number.isNaN(n) || n < 0) return fallback;
  return Math.min(Math.round(n), 24 * 30);
}

/** Turn stored upload path into a browser-usable URL for image preview in the admin app. */
function resolveUploadedAssetUrlForPreview(pathOrUrl: string): string {
  if (!pathOrUrl) return '';
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) return pathOrUrl;
  const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
  const origin = apiBase.replace(/\/?api\/?$/, '');
  return pathOrUrl.startsWith('/') ? `${origin}${pathOrUrl}` : `${origin}/${pathOrUrl}`;
}

function SettingsCollapsibleSection({
  id,
  title,
  summary,
  defaultOpen = false,
  children,
}: {
  id: string;
  title: string;
  summary?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const sectionId = id;

  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        id={`section-trigger-${sectionId}`}
        aria-expanded={open}
        aria-controls={`section-panel-${sectionId}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 px-4 py-4 text-left hover:bg-gray-50/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-orange focus-visible:ring-inset"
      >
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {summary ? <p className="text-sm text-gray-600 mt-1">{summary}</p> : null}
        </div>
        <ChevronDownIcon
          className={`w-5 h-5 text-gray-500 shrink-0 mt-0.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      <div
        id={`section-panel-${sectionId}`}
        role="region"
        aria-labelledby={`section-trigger-${sectionId}`}
        hidden={!open}
        className={open ? 'border-t border-gray-100' : ''}
      >
        {open ? <div className="px-4 py-4 pt-5">{children}</div> : null}
      </div>
    </section>
  );
}

export default function Settings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<AppSettings>({ orderingEnabled: false, chefOnDemandEnabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [homeCategoryIds, setHomeCategoryIds] = useState<string[]>([]);
  const [allMainCategories, setAllMainCategories] = useState<Category[]>([]);
  const [homeCategoriesLoading, setHomeCategoriesLoading] = useState(false);
  const [homeCategoriesSaving, setHomeCategoriesSaving] = useState(false);
  const [homeCategoriesDropdownOpen, setHomeCategoriesDropdownOpen] = useState(false);
  const homeCategoriesDropdownRef = useRef<HTMLDivElement>(null);
  const [deliveryEstimatesForm, setDeliveryEstimatesForm] = useState<DeliveryEstimatesValue>(DEFAULT_DELIVERY_ESTIMATES);

  const [chefBadgeLandingForm, setChefBadgeLandingForm] = useState({
    heroImageUrl: '',
    imageBorderRadius: 16,
    headline: '',
    description: '',
  });
  const [uploadingChefBadgeHero, setUploadingChefBadgeHero] = useState(false);

  const [chefApplicationForm, setChefApplicationForm] = useState(EMPTY_CHEF_APPLICATION_FORM);
  const [uploadingChefApplicationHero, setUploadingChefApplicationHero] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (homeCategoriesDropdownRef.current && !homeCategoriesDropdownRef.current.contains(e.target as Node)) {
        setHomeCategoriesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    const d = settings.deliveryEstimates;
    if (d && typeof d === 'object') {
      setDeliveryEstimatesForm({
        mealBoxMinHours: parsePositiveHour(d.mealBoxMinHours, DEFAULT_DELIVERY_ESTIMATES.mealBoxMinHours),
        mealBoxMaxHours: parsePositiveHour(d.mealBoxMaxHours, DEFAULT_DELIVERY_ESTIMATES.mealBoxMaxHours),
        preparedMealMinHours: parsePositiveHour(d.preparedMealMinHours, DEFAULT_DELIVERY_ESTIMATES.preparedMealMinHours),
        preparedMealMaxHours: parsePositiveHour(d.preparedMealMaxHours, DEFAULT_DELIVERY_ESTIMATES.preparedMealMaxHours),
      });
    }
  }, [settings.deliveryEstimates]);

  useEffect(() => {
    const c = settings.chefBadgeLanding;
    if (c == null || typeof c !== 'object') return;
    setChefBadgeLandingForm({
      heroImageUrl: typeof c.heroImageUrl === 'string' ? c.heroImageUrl : '',
      imageBorderRadius:
        typeof c.imageBorderRadius === 'number' && !Number.isNaN(c.imageBorderRadius)
          ? Math.min(48, Math.max(0, Math.round(c.imageBorderRadius)))
          : 16,
      headline: typeof c.headline === 'string' ? c.headline : '',
      description: typeof c.description === 'string' ? c.description : '',
    });
  }, [settings.chefBadgeLanding]);

  useEffect(() => {
    const c = settings.chefApplicationConfig;
    if (c == null || typeof c !== 'object') {
      setChefApplicationForm({ ...EMPTY_CHEF_APPLICATION_FORM });
      return;
    }
    const str = (k: keyof ChefApplicationConfigValue) =>
      typeof c[k] === 'string' ? (c[k] as string) : '';
    setChefApplicationForm({
      termsTitle: str('termsTitle'),
      termsBody: str('termsBody'),
      guidelinesTitle: str('guidelinesTitle'),
      guidelinesBody: str('guidelinesBody'),
      successHeroImageUrl: str('successHeroImageUrl'),
      successReviewDurationText: str('successReviewDurationText'),
      successDescriptionBefore: str('successDescriptionBefore'),
      successDescriptionAfter: str('successDescriptionAfter'),
      reapplyCooldownDays: parseReapplyCooldownDays(c.reapplyCooldownDays),
    });
  }, [settings.chefApplicationConfig]);

  useEffect(() => {
    loadHomeCategoriesConfig();
  }, []);

  const loadHomeCategoriesConfig = async () => {
    try {
      setHomeCategoriesLoading(true);
      const [idsRes, catsRes] = await Promise.all([
        api.get('/admin/config/home-categories'),
        api.get('/admin/categories', { params: { type: 'main', limit: 100 } }),
      ]);
      if (idsRes.data.success && Array.isArray(idsRes.data.data)) {
        setHomeCategoryIds(idsRes.data.data);
      }
      const catList = catsRes.data?.data;
      if (Array.isArray(catList)) {
        setAllMainCategories(catList);
      }
    } catch (e) {
      console.error('Failed to load home categories config', e);
    } finally {
      setHomeCategoriesLoading(false);
    }
  };

  const saveHomeCategories = async () => {
    try {
      setHomeCategoriesSaving(true);
      setMessage(null);
      await api.put('/admin/config/home-categories', { categoryIds: homeCategoryIds });
      setMessage({ type: 'success', text: 'Home categories updated. App will show up to 10 in order.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Failed to save home categories' });
    } finally {
      setHomeCategoriesSaving(false);
    }
  };

  const removeHomeCategory = (index: number) => {
    setHomeCategoryIds((prev) => prev.filter((_, i) => i !== index));
  };

  const moveHomeCategory = (index: number, up: boolean) => {
    const next = index + (up ? -1 : 1);
    if (next < 0 || next >= homeCategoryIds.length) return;
    setHomeCategoryIds((prev) => {
      const arr = [...prev];
      [arr[index], arr[next]] = [arr[next], arr[index]];
      return arr;
    });
  };

  const categoryById = (id: string) => allMainCategories.find((c) => c._id === id);
  const isSelected = (id: string) => homeCategoryIds.includes(id);
  const toggleCategory = (id: string) => {
    setHomeCategoryIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 10) return prev;
      return [...prev, id];
    });
  };
  /** Selected categories in display order */
  const selectedCategoriesOrdered = homeCategoryIds
    .map((id) => categoryById(id))
    .filter(Boolean) as Category[];

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/settings');
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error: any) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key: string, value: any) => {
    try {
      setSaving(true);
      setMessage(null);
      const response = await api.put(`/admin/settings/${key}`, { value });
      if (response.data.success) {
        setSettings(prev => ({ ...prev, [key]: value }));
        setMessage({ type: 'success', text: 'Setting updated successfully' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error: any) {
      console.error('Error updating setting:', error);
      setMessage({ type: 'error', text: 'Failed to update setting' });
    } finally {
      setSaving(false);
    }
  };

  const paymentMethods: PaymentMethodsConfig = {
    ...DEFAULT_PAYMENT_METHODS,
    ...(settings.paymentMethods || {}),
  };

  const setPaymentMethodEnabled = (key: keyof PaymentMethodsConfig, enabled: boolean) => {
    const next = { ...paymentMethods, [key]: enabled };
    updateSetting('paymentMethods', next);
  };

  const appColors = { ...DEFAULT_APP_COLORS, ...(settings.appColors || {}) };

  const setAppColor = (key: string, hex: string) => {
    const next = { ...appColors, [key]: hex.startsWith('#') ? hex : `#${hex}` };
    setSettings(prev => ({ ...prev, appColors: next }));
  };

  const saveAppColors = async () => {
    await updateSetting('appColors', appColors);
  };

  type ChefBadgeForm = typeof chefBadgeLandingForm;

  const commitChefBadgeLanding = async (override?: Partial<ChefBadgeForm>) => {
    const merged = { ...chefBadgeLandingForm, ...override };
    const r = Number(merged.imageBorderRadius);
    const imageBorderRadius = Number.isFinite(r) ? Math.min(48, Math.max(0, Math.round(r))) : 16;
    await updateSetting('chefBadgeLanding', {
      heroImageUrl: merged.heroImageUrl.trim(),
      imageBorderRadius,
      headline: merged.headline.trim(),
      description: merged.description.trim(),
    });
  };

  const saveChefBadgeLanding = async () => {
    await commitChefBadgeLanding();
  };

  const handleChefBadgeHeroFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setUploadingChefBadgeHero(true);
    setMessage(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'chef-badge-landing');
      fd.append('type', 'image');
      const res = await api.post('/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.url;
      if (!res.data?.success || typeof url !== 'string' || !url) {
        throw new Error('Upload failed');
      }
      await commitChefBadgeLanding({ heroImageUrl: url });
      setMessage({ type: 'success', text: 'Hero image uploaded and saved.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      console.error(err);
      setMessage({
        type: 'error',
        text: err?.response?.data?.message || err?.message || 'Failed to upload image',
      });
    } finally {
      setUploadingChefBadgeHero(false);
    }
  };

  const clearChefBadgeHeroImage = async () => {
    if (!chefBadgeLandingForm.heroImageUrl.trim()) return;
    try {
      await commitChefBadgeLanding({ heroImageUrl: '' });
      setMessage({ type: 'success', text: 'Hero image removed.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to remove image' });
    }
  };

  type ChefApplicationForm = typeof EMPTY_CHEF_APPLICATION_FORM;

  const commitChefApplicationConfig = async (override?: Partial<ChefApplicationForm>) => {
    const merged = { ...chefApplicationForm, ...override };
    await updateSetting('chefApplicationConfig', {
      termsTitle: merged.termsTitle.trim(),
      termsBody: merged.termsBody.trim(),
      guidelinesTitle: merged.guidelinesTitle.trim(),
      guidelinesBody: merged.guidelinesBody.trim(),
      successHeroImageUrl: merged.successHeroImageUrl.trim(),
      successReviewDurationText: merged.successReviewDurationText.trim(),
      successDescriptionBefore: merged.successDescriptionBefore.trim(),
      successDescriptionAfter: merged.successDescriptionAfter.trim(),
      reapplyCooldownDays: parseReapplyCooldownDays(merged.reapplyCooldownDays),
    });
  };

  const saveChefApplicationConfig = async () => {
    await commitChefApplicationConfig();
  };

  const handleChefApplicationHeroFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setUploadingChefApplicationHero(true);
    setMessage(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'chef-application-success');
      fd.append('type', 'image');
      const res = await api.post('/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.url;
      if (!res.data?.success || typeof url !== 'string' || !url) {
        throw new Error('Upload failed');
      }
      await commitChefApplicationConfig({ successHeroImageUrl: url });
      setMessage({ type: 'success', text: 'Success screen image uploaded and saved.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      console.error(err);
      setMessage({
        type: 'error',
        text: err?.response?.data?.message || err?.message || 'Failed to upload image',
      });
    } finally {
      setUploadingChefApplicationHero(false);
    }
  };

  const clearChefApplicationHeroImage = async () => {
    if (!chefApplicationForm.successHeroImageUrl.trim()) return;
    try {
      await commitChefApplicationConfig({ successHeroImageUrl: '' });
      setMessage({ type: 'success', text: 'Success screen image removed.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to remove image' });
    }
  };

  const saveDeliveryEstimates = async () => {
    let { mealBoxMinHours, mealBoxMaxHours, preparedMealMinHours, preparedMealMaxHours } = deliveryEstimatesForm;
    if (mealBoxMaxHours < mealBoxMinHours) mealBoxMaxHours = mealBoxMinHours;
    if (preparedMealMaxHours < preparedMealMinHours) preparedMealMaxHours = preparedMealMinHours;
    await updateSetting('deliveryEstimates', {
      mealBoxMinHours,
      mealBoxMaxHours,
      preparedMealMinHours,
      preparedMealMaxHours,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">App Settings</h2>
        <p className="text-gray-600">Manage application-wide settings</p>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="space-y-3">
        <SettingsCollapsibleSection
          id="ordering-pricing"
          title="Ordering & pricing"
          summary="Enable ordering, delivery and service fees, and VAT."
          defaultOpen
        >
          <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-gray-900">Ordering system</p>
                <p className="text-sm text-gray-600 mt-0.5">
                  When disabled, users see a busy message when trying to purchase recipes.
                </p>
              </div>
              <div className="shrink-0">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.orderingEnabled || false}
                    onChange={(e) => updateSetting('orderingEnabled', e.target.checked)}
                    disabled={saving}
                  />
                  <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-orange" />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {settings.orderingEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2 border-t border-gray-100">
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-gray-900">Delivery fee (EGP)</p>
                <p className="text-sm text-gray-600 mt-0.5">Added to all orders. Default: 15 EGP.</p>
              </div>
              <div className="shrink-0 flex items-center">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.shippingCost ?? 15}
                  onChange={(e) => updateSetting('shippingCost', parseFloat(e.target.value) || 0)}
                  disabled={saving}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                  placeholder="15.00"
                />
                <span className="ml-2 text-sm text-gray-600">EGP</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2 border-t border-gray-100">
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-gray-900">Service fee (EGP)</p>
                <p className="text-sm text-gray-600 mt-0.5">Charged on all orders. Default: 5 EGP.</p>
              </div>
              <div className="shrink-0 flex items-center">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.serviceFee ?? 5}
                  onChange={(e) => updateSetting('serviceFee', parseFloat(e.target.value) || 0)}
                  disabled={saving}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                  placeholder="5.00"
                />
                <span className="ml-2 text-sm text-gray-600">EGP</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2 border-t border-gray-100">
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-gray-900">VAT</p>
                <p className="text-sm text-gray-600 mt-0.5">Default: 14% (Egypt food delivery).</p>
              </div>
              <div className="shrink-0 flex flex-wrap items-center gap-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.vatEnabled ?? true}
                    onChange={(e) => updateSetting('vatEnabled', e.target.checked)}
                    disabled={saving}
                  />
                  <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-orange" />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {settings.vatEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
                {settings.vatEnabled && (
                  <div className="flex items-center">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={settings.vatPercentage ?? 14}
                      onChange={(e) => updateSetting('vatPercentage', parseFloat(e.target.value) || 0)}
                      disabled={saving}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                      placeholder="14"
                    />
                    <span className="ml-2 text-sm text-gray-600">%</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </SettingsCollapsibleSection>

        <SettingsCollapsibleSection
          id="delivery-timing"
          title="Delivery timing"
          summary="Order ETAs and recipe-screen delivery windows (hours)."
        >
          <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-gray-900">Default estimated delivery (minutes)</p>
                <p className="text-sm text-gray-600 mt-0.5">
                  Shown for new orders only; does not change existing orders. Default: 45.
                </p>
              </div>
              <div className="shrink-0 flex items-center">
                <input
                  type="number"
                  min="5"
                  max="300"
                  step="5"
                  value={settings.defaultDeliveryMinutes ?? 45}
                  onChange={(e) => updateSetting('defaultDeliveryMinutes', parseInt(e.target.value, 10) || 45)}
                  disabled={saving}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                  placeholder="45"
                />
                <span className="ml-2 text-sm text-gray-600">min</span>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-100">
              <p className="text-base font-semibold text-gray-900">Recipe detail — delivery windows (hours)</p>
              <p className="text-sm text-gray-600 mt-1 mb-4">
                Min/max for &quot;Estimated delivery&quot; on the recipe screen (meal box vs prepared). Save applies via
                order-fees API.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                <div>
                  <p className="text-sm font-medium text-gray-800 mb-2">Meal box</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs text-gray-600">Min</label>
                    <input
                      type="number"
                      min={0}
                      max={720}
                      value={deliveryEstimatesForm.mealBoxMinHours}
                      onChange={(e) =>
                        setDeliveryEstimatesForm((prev) => ({
                          ...prev,
                          mealBoxMinHours: parsePositiveHour(e.target.value, prev.mealBoxMinHours),
                        }))
                      }
                      disabled={saving}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange"
                    />
                    <label className="text-xs text-gray-600">Max</label>
                    <input
                      type="number"
                      min={0}
                      max={720}
                      value={deliveryEstimatesForm.mealBoxMaxHours}
                      onChange={(e) =>
                        setDeliveryEstimatesForm((prev) => ({
                          ...prev,
                          mealBoxMaxHours: parsePositiveHour(e.target.value, prev.mealBoxMaxHours),
                        }))
                      }
                      disabled={saving}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange"
                    />
                    <span className="text-xs text-gray-500">hours</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800 mb-2">Prepared meal</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs text-gray-600">Min</label>
                    <input
                      type="number"
                      min={0}
                      max={720}
                      value={deliveryEstimatesForm.preparedMealMinHours}
                      onChange={(e) =>
                        setDeliveryEstimatesForm((prev) => ({
                          ...prev,
                          preparedMealMinHours: parsePositiveHour(e.target.value, prev.preparedMealMinHours),
                        }))
                      }
                      disabled={saving}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange"
                    />
                    <label className="text-xs text-gray-600">Max</label>
                    <input
                      type="number"
                      min={0}
                      max={720}
                      value={deliveryEstimatesForm.preparedMealMaxHours}
                      onChange={(e) =>
                        setDeliveryEstimatesForm((prev) => ({
                          ...prev,
                          preparedMealMaxHours: parsePositiveHour(e.target.value, prev.preparedMealMaxHours),
                        }))
                      }
                      disabled={saving}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange"
                    />
                    <span className="text-xs text-gray-500">hours</span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => saveDeliveryEstimates()}
                disabled={saving}
                className="mt-4 px-4 py-2 bg-primary-orange text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
              >
                Save delivery windows
              </button>
            </div>
          </div>
        </SettingsCollapsibleSection>

        <SettingsCollapsibleSection
          id="checkout-payments"
          title="Checkout & payments"
          summary={t('settings.paymentMethodsDesc')}
        >
          <p className="text-sm font-medium text-gray-800 mb-4">{t('settings.paymentMethods')}</p>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={paymentMethods.cod !== false}
                  onChange={(e) => setPaymentMethodEnabled('cod', e.target.checked)}
                  disabled={saving}
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-orange" />
                <span className="ml-3 text-sm font-medium text-gray-700">{t('settings.cashOnDelivery')}</span>
              </label>
              <span className="text-sm text-gray-500">{paymentMethods.cod !== false ? t('settings.enabled') : t('settings.disabled')}</span>
            </div>
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={paymentMethods.instapay !== false}
                  onChange={(e) => setPaymentMethodEnabled('instapay', e.target.checked)}
                  disabled={saving}
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-orange" />
                <span className="ml-3 text-sm font-medium text-gray-700">{t('settings.instapay')}</span>
              </label>
              <span className="text-sm text-gray-500">{paymentMethods.instapay !== false ? t('settings.enabled') : t('settings.disabled')}</span>
            </div>
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={paymentMethods.wallet !== false}
                  onChange={(e) => setPaymentMethodEnabled('wallet', e.target.checked)}
                  disabled={saving}
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-orange" />
                <span className="ml-3 text-sm font-medium text-gray-700">{t('settings.mobileWallet')}</span>
              </label>
              <span className="text-sm text-gray-500">{paymentMethods.wallet !== false ? t('settings.enabled') : t('settings.disabled')}</span>
            </div>
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={paymentMethods.paymob !== false}
                  onChange={(e) => setPaymentMethodEnabled('paymob', e.target.checked)}
                  disabled={saving}
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-orange" />
                <span className="ml-3 text-sm font-medium text-gray-700">{t('settings.paymobCard')}</span>
              </label>
              <span className="text-sm text-gray-500">{paymentMethods.paymob !== false ? t('settings.enabled') : t('settings.disabled')}</span>
            </div>
          </div>
        </SettingsCollapsibleSection>

        <SettingsCollapsibleSection
          id="home-discovery"
          title="Home & discovery"
          summary="Up to 10 main categories on the app home screen, in order."
        >
        <p className="text-sm text-gray-600 mb-4">
          Select categories below. First in the list appears left on the home screen. Use arrows to reorder.
        </p>
        {homeCategoriesLoading ? (
          <p className="text-gray-500">Loading…</p>
        ) : (
          <>
            <div className="relative inline-block" ref={homeCategoriesDropdownRef}>
              <button
                type="button"
                onClick={() => setHomeCategoriesDropdownOpen((o) => !o)}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-orange focus:ring-offset-2"
              >
                <span>
                  {homeCategoryIds.length === 0
                    ? 'Select categories'
                    : `${homeCategoryIds.length} categor${homeCategoryIds.length === 1 ? 'y' : 'ies'} selected`}
                </span>
                <ChevronDownIcon
                  className={`w-5 h-5 transition-transform ${homeCategoriesDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {homeCategoriesDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 z-20 min-w-[280px] max-h-[320px] overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg py-2">
                  <div className="grid grid-cols-1 gap-0.5 px-2">
                    {allMainCategories
                      .slice()
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map((cat) => {
                        const id = cat._id;
                        const selected = isSelected(id);
                        return (
                          <label
                            key={id}
                            className={`flex items-center gap-2 py-2 px-3 rounded-md cursor-pointer ${
                              selected ? 'bg-orange-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleCategory(id)}
                              disabled={!selected && homeCategoryIds.length >= 10}
                              className="h-4 w-4 rounded border-gray-300 text-primary-orange focus:ring-primary-orange"
                            />
                            <span className="text-sm font-medium text-gray-900 truncate">{cat.name}</span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {selectedCategoriesOrdered.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Order (first = left on app)</p>
                <ul className="space-y-2">
                  {selectedCategoriesOrdered.map((cat, index) => (
                    <li
                      key={cat._id}
                      className="flex items-center gap-2 py-2 px-3 rounded-lg border bg-orange-50 border-orange-200"
                    >
                      <span className="flex-1 font-medium text-gray-900">{cat.name}</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveHomeCategory(index, true)}
                          disabled={index === 0}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-40"
                          title="Move up"
                        >
                          <ChevronUpIcon className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveHomeCategory(index, false)}
                          disabled={index === selectedCategoriesOrdered.length - 1}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-40"
                          title="Move down"
                        >
                          <ChevronDownIcon className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeHomeCategory(index)}
                          className="p-1 rounded hover:bg-red-100 text-red-600"
                          title="Remove"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4">
              <button
                type="button"
                onClick={saveHomeCategories}
                disabled={homeCategoriesSaving}
                className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-orange focus:ring-offset-2"
              >
                {homeCategoriesSaving ? 'Saving…' : 'Save home categories'}
              </button>
            </div>
          </>
        )}
        </SettingsCollapsibleSection>

        <SettingsCollapsibleSection
          id="chef-on-demand"
          title="Chef on demand"
          summary="Home discovery card and profile entries (Chef Bookings, Clients Bookings, Chef Settings) in the mobile app."
        >
          <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-gray-900">Chef on demand</p>
                <p className="text-sm text-gray-600 mt-0.5">
                  When disabled, the Chef on Demand card on the home screen is hidden, and Chef Bookings, Clients
                  Bookings, and Chef Settings are removed from the user profile.
                </p>
              </div>
              <div className="shrink-0">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.chefOnDemandEnabled !== false}
                    onChange={(e) => updateSetting('chefOnDemandEnabled', e.target.checked)}
                    disabled={saving}
                  />
                  <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-orange" />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {settings.chefOnDemandEnabled !== false ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </SettingsCollapsibleSection>

        <SettingsCollapsibleSection
          id="chef-program-mobile"
          title="Chef program — landing (mobile)"
          summary="Screen before the user taps Start: hero image, radius, title, and description (chefBadgeLanding)."
        >
        <p className="text-sm text-gray-600 mb-4 max-w-2xl">
          Upload a hero image for the mobile landing screen. Empty title or description uses the app default. Uploading
          saves the image immediately; use the button below to save radius and copy changes.
        </p>
        <div className="space-y-4 max-w-2xl">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Hero image</label>
            <div className="flex flex-wrap items-start gap-4">
              <div
                className="w-full max-w-xs aspect-[16/10] rounded-xl border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center"
                style={{ borderRadius: Math.min(48, Math.max(0, chefBadgeLandingForm.imageBorderRadius || 16)) }}
              >
                {chefBadgeLandingForm.heroImageUrl ? (
                  <img
                    src={resolveUploadedAssetUrlForPreview(chefBadgeLandingForm.heroImageUrl)}
                    alt="Chef badge landing preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-sm text-gray-400 px-4 text-center">No image yet</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex">
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    disabled={uploadingChefBadgeHero || saving}
                    onChange={handleChefBadgeHeroFileChange}
                  />
                  <span className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                    {uploadingChefBadgeHero ? 'Uploading…' : 'Upload image'}
                  </span>
                </label>
                {chefBadgeLandingForm.heroImageUrl ? (
                  <button
                    type="button"
                    onClick={() => clearChefBadgeHeroImage()}
                    disabled={saving || uploadingChefBadgeHero}
                    className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 text-left"
                  >
                    Remove image
                  </button>
                ) : null}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image corner radius (0–48 px)</label>
            <input
              type="number"
              min={0}
              max={48}
              value={chefBadgeLandingForm.imageBorderRadius}
              onChange={(e) =>
                setChefBadgeLandingForm((prev) => ({
                  ...prev,
                  imageBorderRadius: parseInt(e.target.value, 10) || 0,
                }))
              }
              className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title (optional)</label>
            <input
              type="text"
              value={chefBadgeLandingForm.headline}
              onChange={(e) => setChefBadgeLandingForm((prev) => ({ ...prev, headline: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange"
              placeholder="Leave empty for app default"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              rows={4}
              value={chefBadgeLandingForm.description}
              onChange={(e) => setChefBadgeLandingForm((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange"
              placeholder="Leave empty for app default"
            />
          </div>
          <button
            type="button"
            onClick={saveChefBadgeLanding}
            disabled={saving}
            className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-orange focus:ring-offset-2"
          >
            {saving ? 'Saving…' : 'Save Chef Badge landing'}
          </button>
        </div>
        </SettingsCollapsibleSection>

        <SettingsCollapsibleSection
          id="chef-application-config"
          title="Chef application — form & success (mobile)"
          summary="Terms and content guidelines on the last step, plus the image and copy on the “Application submitted” screen (chefApplicationConfig)."
        >
          <p className="text-sm text-gray-600 mb-4 max-w-3xl">
            The app loads this from public config. Link labels and full text for Terms and Content Guidelines open in a
            sheet when the user taps them on the final application step. The success section controls the hero image above
            the confirmation message after submit.
          </p>
          <div className="space-y-6 max-w-3xl">
            <div className="rounded-lg border border-amber-100 bg-amber-50/50 px-4 py-3 max-w-lg">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Reapply cooldown after rejection (days)
              </label>
              <p className="text-xs text-gray-600 mb-2">
                Full calendar days a user must wait after a rejected chef application before submitting again. Use{' '}
                <span className="font-mono">0</span> to allow immediate reapply.
              </p>
              <input
                type="number"
                min={0}
                max={365}
                value={chefApplicationForm.reapplyCooldownDays}
                onChange={(e) =>
                  setChefApplicationForm((p) => ({
                    ...p,
                    reapplyCooldownDays: parseReapplyCooldownDays(e.target.value),
                  }))
                }
                className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange"
              />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Last step — legal copy</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms — link label (optional)</label>
                  <input
                    type="text"
                    value={chefApplicationForm.termsTitle}
                    onChange={(e) => setChefApplicationForm((p) => ({ ...p, termsTitle: e.target.value }))}
                    className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange"
                    placeholder="e.g. Terms"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms — full text</label>
                  <textarea
                    rows={6}
                    value={chefApplicationForm.termsBody}
                    onChange={(e) => setChefApplicationForm((p) => ({ ...p, termsBody: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange font-mono text-sm"
                    placeholder="Shown when the user taps the Terms link."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content guidelines — link label (optional)
                  </label>
                  <input
                    type="text"
                    value={chefApplicationForm.guidelinesTitle}
                    onChange={(e) => setChefApplicationForm((p) => ({ ...p, guidelinesTitle: e.target.value }))}
                    className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange"
                    placeholder="e.g. Content Guidelines"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content guidelines — full text</label>
                  <textarea
                    rows={6}
                    value={chefApplicationForm.guidelinesBody}
                    onChange={(e) => setChefApplicationForm((p) => ({ ...p, guidelinesBody: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange font-mono text-sm"
                    placeholder="Shown when the user taps the Content Guidelines link."
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">After submit — success screen</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hero image (optional)</label>
                  <div className="flex flex-wrap items-start gap-4">
                    <div className="w-full max-w-xs aspect-[16/10] rounded-xl border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center">
                      {chefApplicationForm.successHeroImageUrl ? (
                        <img
                          src={resolveUploadedAssetUrlForPreview(chefApplicationForm.successHeroImageUrl)}
                          alt="Application submitted preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-sm text-gray-400 px-4 text-center">No image — app uses icon only</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="inline-flex">
                        <input
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          disabled={uploadingChefApplicationHero || saving}
                          onChange={handleChefApplicationHeroFileChange}
                        />
                        <span className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                          {uploadingChefApplicationHero ? 'Uploading…' : 'Upload image'}
                        </span>
                      </label>
                      {chefApplicationForm.successHeroImageUrl ? (
                        <button
                          type="button"
                          onClick={() => clearChefApplicationHeroImage()}
                          disabled={saving || uploadingChefApplicationHero}
                          className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 text-left"
                        >
                          Remove image
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Review time — highlighted in orange (e.g. 3–5 business days)
                  </label>
                  <input
                    type="text"
                    value={chefApplicationForm.successReviewDurationText}
                    onChange={(e) =>
                      setChefApplicationForm((p) => ({ ...p, successReviewDurationText: e.target.value }))
                    }
                    className="w-full max-w-lg px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange"
                    placeholder="3-5 business days"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description — text before the orange duration
                  </label>
                  <textarea
                    rows={2}
                    value={chefApplicationForm.successDescriptionBefore}
                    onChange={(e) =>
                      setChefApplicationForm((p) => ({ ...p, successDescriptionBefore: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange text-sm"
                    placeholder="Our team will review your profile and get back to you within "
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description — text after the orange duration
                  </label>
                  <textarea
                    rows={2}
                    value={chefApplicationForm.successDescriptionAfter}
                    onChange={(e) =>
                      setChefApplicationForm((p) => ({ ...p, successDescriptionAfter: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange text-sm"
                    placeholder=" Keep an eye on your notifications and email for the next steps."
                  />
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={saveChefApplicationConfig}
              disabled={saving}
              className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-orange focus:ring-offset-2"
            >
              {saving ? 'Saving…' : 'Save chef application config'}
            </button>
          </div>
        </SettingsCollapsibleSection>

        <SettingsCollapsibleSection
          id="mobile-appearance"
          title="Mobile app appearance"
          summary="Theme colors sent to the app via the public config API (defaults apply if the API fails)."
        >
        {APP_COLOR_GROUPS.map((group) => (
          <div key={group.label} className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-2">{group.label}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {group.keys.map((key) => (
                <div key={key} className="flex items-center gap-2">
                  <input
                    type="color"
                    value={appColors[key] || DEFAULT_APP_COLORS[key]}
                    onChange={(e) => setAppColor(key, e.target.value)}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer p-0"
                    title={getColorLabel(key)}
                  />
                  <div className="flex-1 min-w-0">
                    <label className="text-xs text-gray-500 block truncate">{getColorLabel(key)}</label>
                    <input
                      type="text"
                      value={(appColors[key] || '').replace(/^#/, '')}
                      onChange={(e) => setAppColor(key, '#' + e.target.value.replace(/^#/, ''))}
                      placeholder={DEFAULT_APP_COLORS[key]?.replace(/^#/, '')}
                      className="w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-orange"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="pt-2">
          <button
            type="button"
            onClick={saveAppColors}
            disabled={saving}
            className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-orange focus:ring-offset-2"
          >
            {saving ? 'Saving…' : 'Save app colors'}
          </button>
        </div>
        </SettingsCollapsibleSection>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Note:</h4>
        <p className="text-sm text-blue-800">
          Changes to settings take effect immediately. The ordering system can also be controlled at the recipe level using the "Buying Enabled" option in individual recipes.
        </p>
      </div>
    </div>
  );
}
